import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft } from '@tabler/icons-react';
import { theme } from 'antd';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface BackButtonProps {
  // Where to go when there's no in-app history to pop (direct URL hit,
  // refresh, opened from an external link) -- a logical parent route, not
  // necessarily the literal previous page.
  fallbackPath: string;
  // Only pass this on the rare page whose title reads as ambiguous without
  // it (per-page judgement call) -- every other caller leaves it icon-only.
  label?: string;
}

/**
 * Icon-only "go back" control meant to sit immediately to the left of a
 * page's main title. Reused across every detail/subsection page instead of
 * each one hand-rolling its own back button (see e.g. the old
 * HostProfileEditPage one this replaces). Never used on root pages reached
 * directly from the header nav (Explore, Mis reservas, Mis espacios).
 */
const BackButton: React.FC<BackButtonProps> = ({ fallbackPath, label }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = theme.useToken();
  const [hovering, setHovering] = React.useState(false);

  const handleClick = () => {
    // React Router seeds the very first history entry in a tab/session with
    // the literal key "default" -- if that's still the current entry, there
    // is nothing in-app to pop back to (direct URL hit, refresh, external
    // link), so fall back to a logical parent route instead of a no-op.
    if (location.key === 'default') {
      navigate(fallbackPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      aria-label={translate(t, TRANSLATIONS.BACK)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        border: 'none',
        background: 'none',
        padding: 0,
        cursor: 'pointer',
        color: hovering ? token.colorText : token.colorTextSecondary,
      }}
    >
      <IconArrowLeft size={20} />
      {label && <span style={{ fontSize: 14 }}>{label}</span>}
    </button>
  );
};

export default BackButton;
